<template>
<div class="shape-months">
  <!-- THE YEAR, MONTH BY MONTH, AND WHAT OF EACH MONTH WAS NOT THE BUSINESS SELLING. Two
       statements about the same twelve months in one stack: what comes back every year, and what
       happened once. They were two plots, side by side inside one, each month split between them
       — which drew the two years honestly and made the one reading that matters hard to take,
       the reading being what the two of them come to TOGETHER in a month. Stacked, that is the
       height of the column.

       THE ROW HAS A LINE THROUGH IT AND BOTH HALVES ARE REAL. A season takes revenue out of a
       month as readily as it adds any — that is what a season IS, revenue moved between the
       months rather than made — so the axis is symmetric and a month hangs under the line as
       naturally as it stands over it. Segments stack outward from that line on either side, in
       the same order both ways, so the distance from the line to a component is the same
       whichever side it fell on.

       The columns are normalised against the largest month, so nothing can be READ off one on
       its own: the height says the shape and the figure over it says the amount. Same bargain
       the revenue row and the spend row above it make, and the same picture, so a reader who has
       followed one has followed this. -->
  <div class="clone-metric__bars shape-months__bars" :style="barGap">
    <!-- The line the months stand on, in the middle: this row is signed and the two sides of it
         are the same size, so it is drawn always rather than only where something hangs. -->
    <span class="clone-metric__bar-zero"></span>
    <component
      :is="monthsEditable && !entry.apart ? 'button' : 'span'"
      v-for="(entry, index) in columns"
      :key="`shape_bar_${entry.key}`"
      :type="monthsEditable && !entry.apart ? 'button' : null"
      class="clone-metric__bar"
      :class="{
        'clone-metric__bar--live': monthsEditable && !entry.apart,
        'clone-metric__bar--editing': editing === index,
        'clone-metric__bar--apart': entry.apart
      }"
      :style="entry.apart ? apartStyle : null"
      :aria-label="entry.totalTitle"
      @mouseenter="hovered = index"
      @mouseleave="onLeave(index)"
      @focus="hovered = index"
      @blur="onLeave(index)"
      @click="onBar(index)">
      <!-- What the month gained, over the line and read upwards. The markup runs the other way
           round from the stack — this box is laid out from the top down and packed to its foot —
           so the caller hands the segments in top-down order and the one nearest the line is the
           last of them. -->
      <span class="clone-metric__bar-up">
        <span v-if="showAmounts" class="clone-metric__bar-value">{{ entry.amount }}</span>
        <span
          v-for="segment in entry.up"
          :key="`shape_up_${entry.key}_${segment.key}`"
          class="clone-metric__bar-fill"
          :class="{ 'clone-metric__bar-fill--hatched': segment.hatched }"
          :style="{ flexBasis: segment.basis + '%', backgroundColor: segment.color }"></span>
      </span>
      <!-- And what it gave up, hanging under the line and read downwards, which is why this box
           starts where the other one ends: here the first segment handed in is the one nearest
           the line. -->
      <span class="clone-metric__bar-down">
        <span
          v-for="segment in entry.down"
          :key="`shape_down_${entry.key}_${segment.key}`"
          class="clone-metric__bar-fill"
          :class="{ 'clone-metric__bar-fill--hatched': segment.hatched }"
          :style="{ flexBasis: segment.basis + '%', backgroundColor: segment.color }"></span>
      </span>
    </component>

    <!-- WHAT THE COLUMN IS MADE OF, NAMED AND IN ITS OWN FILL — AND, WHERE THE ROW MAY BE TYPED
         INTO, WHERE IT IS SAID. A native title is one grey line of arithmetic and this is a
         stack of two claims with signs on them: each row carries the swatch the band it names is
         drawn in, so a row of the card and a band of the column are one object.

         ONE CARD AND NOT TWO. Hovering opens it as a reading; pressing the column PINS the same
         card and turns each figure in it into a field. That is the whole of the gesture, and it
         is the one the Media Spend row above already answers to — a band two pixels tall is not
         a thing anybody can be asked to hit, and a form that opened one row at a time would ask
         the reader to press the same column twice.

         Read, it lists only what claimed something: a month with no spot event in it has nothing
         to say about spot events. Typed, it lists both — a month is also where a claim that was
         never made is made.

         Deaf to the pointer while it is a reading, so moving onto it never counts as leaving the
         column that opened it, and alive the moment it is a form. -->
    <div
      v-if="card"
      ref="card"
      class="clone-bar-tip"
      :class="{ 'clone-bar-tip--live': editing !== null }"
      :style="tipStyle"
      @focusout="onCardBlur">
      <div class="clone-bar-tip__head">{{ card.month }}</div>
      <div
        v-for="(part, position) in cardParts"
        :key="`${card.key}_${part.index}`"
        class="clone-bar-tip__row"
        :title="part.info">
        <span
          class="clone-bar-tip__dot"
          :class="{ 'clone-bar-tip__dot--hatched': part.hatched }"
          :style="{ backgroundColor: part.color }"></span>
        <span class="clone-bar-tip__name">{{ part.name }}</span>
        <!-- TAB WALKS THE CARD AND THEN THE ROW. Inside the card it is the browser's own Tab and
             nothing is bound at all — the fields are in the DOM in the order they are stacked
             in, which is the order they are read in. Off the last one it becomes the row's own
             gesture: what was typed is applied and the NEXT month opens, so a year is stated by
             typing rather than by pressing twelve columns with the hand off the keyboard between
             each two. Shift is the same walk backwards, off the first field to the month
             before. -->
        <input
          v-if="editing !== null"
          :ref="`field_${part.index}`"
          class="clone-bar-tip__field"
          :value="part.value"
          :aria-label="`${card.month} — ${part.name}`"
          @keydown.esc.exact.prevent="closeCard"
          @keydown.enter.exact.prevent="commitCard"
          @keydown.tab.exact="onTab($event, position, 1)"
          @keydown.shift.tab="onTab($event, position, -1)">
        <span v-else class="clone-bar-tip__value">{{ part.shown }}</span>
      </div>
      <!-- The sum, under a rule: it is not a third claim, it is what the two above it come to —
           the whole of what this month was NOT the business selling on its own. While the card is
           a form it is what the column came in at: both fields are committed together, on the way
           out, so there is no half-typed month for it to be a sum of. -->
      <div class="clone-bar-tip__row clone-bar-tip__row--sum">
        <span class="clone-bar-tip__dot clone-bar-tip__dot--none"></span>
        <span class="clone-bar-tip__name">{{ card.totalLabel }}</span>
        <span class="clone-bar-tip__value">{{ card.amount }}</span>
      </div>
      <div v-if="editing !== null && keysHint" class="clone-bar-tip__keys">{{ keysHint }}</div>
    </div>
  </div>

  <!-- One letter per month, in a row of its own with the plot's own gap, so a letter stands
       under the column it names without taking any of the height the bars are read against. The
       year names itself in full: it is not one of twelve of anything, so there is no initial to
       take, and it reads in the small grey a reading wears rather than as a thirteenth tick. -->
  <div class="clone-metric__bar-axis" :class="{ 'clone-metric__bar-axis--dense': dense }" :style="barGap">
    <span
      v-for="entry in columns"
      :key="`shape_axis_${entry.key}`"
      class="clone-metric__bar-tick"
      :class="{ 'clone-metric__bar-tick--apart': entry.apart }"
      :style="entry.apart ? apartStyle : null"
      :title="entry.apart ? entry.totalTitle : entry.month">{{ entry.initial }}</span>
  </div>

  <!-- WHICH FILL IS WHICH. Under the axis and not over the plot: it is read once, when the
       reader first meets a hatched band and wants to know what the hatching means, and then
       never again — which is the position a key earns, at the foot of the picture rather than
       between the picture and its heading. -->
  <div v-if="legend.length" class="shape-months__legend">
    <span
      v-for="row in legend"
      :key="`shape_legend_${row.key}`"
      class="shape-months__legend-key">
      <span
        class="shape-months__swatch"
        :class="{ 'shape-months__swatch--hatched': row.hatched }"
        :style="{ backgroundColor: row.color }"></span>{{ row.name }}
    </span>
  </div>
</div>
</template>

<script>
// The seasonality and the spot events, month by month, stacked in one signed row.
//
// It is EkrmSpendMonths asked about the other statement: same columns, same card under the pointer,
// same card pinned as a form, same Tab through the stack and on to the next month. What is different
// is that this row has a line through the middle of it — a season takes revenue out of a month as
// readily as it adds any, and a spot event can have cost revenue rather than made it — so a column
// stands over the line, hangs under it, or does both.
//
// It owns no arithmetic. Every band arrives as a percentage of the half-region it stands in and a
// colour, every figure arrives formatted, and the year beside the twelve arrives as a thirteenth
// column built the same way.
// That is not tidiness: this project cannot mount a component in a test — vue and
// vue-template-compiler are a patch apart, so @vue/test-utils refuses to load — so anything that
// lives here is untestable and anything that can be pinned by a test has to live somewhere else.
// See EkrmChannelCard for the rule stated in full.

// How wide the card that names a month's two claims is, read and as a form. A field in percent is
// narrower than one in dollars, so the form is narrower than the spend row's.
const TIP_WIDTH = 200
const FORM_WIDTH = 250

// The column the year stands in, and the air between it and December. The two numbers are the
// revenue row's own — see EkrmRevenueCard — which is what puts the rule under this plot and the rule
// under that one on one line and ends both at the same place. The air is taken out of the column's
// margin rather than added to it, the row already carrying a gap between every pair of columns.
const APART_WIDTH = 96
const APART_GAP = 12

export default {
  name: 'EkrmShapeMonths',
  props: {
    // The keys the form answers to, spelled out at the foot of it — Tab through the stack and then
    // on to the next month, Enter to apply, Esc to drop it. The component owns no words.
    keysHint: { type: String, default: '' },
    // [{ key, initial, month, amount, totalLabel, totalTitle, up, down, parts }].
    //
    //   up, down  [{ key, color, hatched, basis }] — percentages of the half-region they stand in,
    //             `up` in MARKUP order (the segment nearest the line last) and `down` in markup
    //             order too (the segment nearest the line first), so each box reads outward from
    //             the line however its own flex is packed
    //   parts     [{ index, name, color, hatched, value, shown, claimed, info }] — the two rows named
    //             and formatted for the card. `value` is what a field OPENS on and `shown` is what
    //             the reading displays; `claimed` is what the reading filters on, and `index` is the
    //             row's position in the caller's own list, which is the only thing a typed figure
    //             comes back as
    //
    // A month that claims nothing still arrives, with segments that come to nothing: a gap in the
    // row is a month, not an absence.
    bars: { type: Array, required: true },
    // THE YEAR AS A THIRTEENTH COLUMN, in the shape of a bar and carrying `apart: true`, or null
    // for a plot that draws the twelve alone. Same keys as one of them, at its own scale: a year of
    // seasonality is not a month of it, and the rule down its left is what says the two axes are not
    // one.
    //
    // It is a READING and not a statement — it is the twelve added up, and the way to change it is
    // to change one of them — so it opens no form, however editable the months are. Its card names
    // the two rows in MONEY, which is the one place on the page they are read apart: a year of
    // seasonality nets to nearly nothing and a year of spot events does not, and that difference is
    // the whole reason they are two rows and not one.
    total: { type: Object, default: null },
    // [{ key, name, color, hatched }] — what the two fills MEAN, under the axis.
    //
    // The row draws two claims in one colour and tells them apart by the fill alone, which is the
    // right weight for the distinction — see the caller, where the argument for one colour is made
    // — but a texture is only a distinction once somebody has been told which is which. The year's
    // own card carries the same two swatches and cannot do this: it has to be opened to be read at
    // all, and the reader who has just drawn their first spot event and is looking at one hatched
    // band for the first time is exactly the reader who does not know there is anything to open.
    //
    // Both rows, always, whether or not either has been drawn: it is a key to the picture and not a
    // reading of it.
    legend: { type: Array, default: function () { return [] } },
    barGap: { type: Object, default: null },
    dense: { type: Boolean, default: false },
    // Whether a month may be SAID rather than only read.
    monthsEditable: { type: Boolean, default: false }
  },
  data () {
    return {
      // The column under the pointer, or null. It names the card standing over the row and nothing
      // else: no figure moves for it.
      hovered: null,
      // The column whose card is a FORM rather than a reading, or null. One at a time, and it
      // outranks the pointer: a card pinned to July must not follow a hand crossing August on its
      // way to the field.
      editing: null,
      // Set for the one flush a commit's redraw takes. Committing moves the row, and the watcher
      // that closes a pinned card whenever the row moves is the one thing that must not close this
      // one before the click that closed it has been handled. See commitCard.
      closing: false,
      // Set while Tab is carrying the card from one month to the next. The rows are keyed by the
      // month, so the field the caret is in is taken out of the DOM and put back one column along —
      // which some browsers report as a focus LEAVING the card, and a focus leaving the card is a
      // commit that would close the month Tab has just opened. See stepCard.
      stepping: false
    }
  },
  computed: {
    // The row as it is drawn: the twelve, and the year beside them where there is one. Everything
    // downstream indexes into this — the card over a column, the axis under one — so there is one
    // list and one set of indices rather than a row with an exception in it.
    columns () {
      return this.total ? this.bars.concat([this.total]) : this.bars
    },
    // The width the year's column takes out of the row, and the air before it. The gap is taken out
    // of the margin because the row already puts one between every pair of columns: what is wanted
    // is twelve pixels from December to the rule, however wide the row's own gap happens to be.
    apartStyle () {
      return {
        flex: `0 0 ${APART_WIDTH}px`,
        marginLeft: `calc(${APART_GAP}px - ${(this.barGap && this.barGap.gap) || '0px'})`
      }
    },
    // How much of the row the year's column and its air take, in pixels. What is left is the twelve
    // months, which is what a card standing over one is placed against.
    apartRoom () {
      return this.total ? APART_WIDTH + APART_GAP : 0
    },
    // The column the card belongs to: the one pinned, or the one under the pointer. Pinned first, so
    // a hand crossing the row on its way to a field cannot take the card off the month it opened.
    card () {
      const at = this.at
      if (at === null) return null
      const bar = this.columns[at]
      return bar && bar.parts ? bar : null
    },
    at () {
      if (this.editing !== null) return this.editing
      return this.hovered
    },
    // Read, only the rows that claimed something; typed, both of them. See the card itself for why
    // the two lists differ. A month that claims nothing at all still opens a card as a form — there
    // is nothing to read there and everything to say.
    cardParts () {
      if (!this.card) return []
      if (this.editing !== null) return this.card.parts
      return this.card.parts.filter(part => part.claimed)
    },
    // Above the chart and centred on its own column, then held between the row's two edges: a
    // January whose card hung off the left would belong to no picture at all. Wider as a form than
    // as a reading, a row that has to hold a field being wider than one that holds a figure.
    tipStyle () {
      const at = this.at
      if (at === null) return null
      const width = this.editing === null ? TIP_WIDTH : FORM_WIDTH
      return {
        width: `${width}px`,
        left: `clamp(0px, calc(${this.centreOf(at)} - ${width / 2}px), calc(100% - ${width}px))`
      }
    },
    // Whether there is room for a figure per column. Twelve numbers fit a year; a window several
    // years wide has columns narrower than a digit, which is the same question `dense` answers.
    showAmounts () {
      return !this.dense && this.bars.every(entry => !!entry.amount)
    }
  },
  methods: {
    // The middle of one column, as a CSS length rather than a percentage. The months share whatever
    // the year's own column leaves — a fixed number of pixels off the row, not a share of it — so a
    // percentage of the row is the wrong unit for anything that has to stand over a month. The year
    // is the last column and is measured back from the right edge for the same reason.
    centreOf (index) {
      const count = this.bars.length
      if (index >= count) return `calc(100% - ${APART_WIDTH / 2}px)`
      const share = count > 0 ? (index + 0.5) / count : 0
      return `calc((100% - ${this.apartRoom}px) * ${share})`
    },
    // Guarded on the column, so the mouseleave of the one being left cannot clear a card the pointer
    // has already opened on the next.
    onLeave (index) {
      if (this.hovered === index) this.hovered = null
    },
    // A column pressed. It pins the card that was already standing over it and turns its figures
    // into fields — nothing has moved yet, and pressing the column that is open closes it again, a
    // press being how it was opened.
    onBar (index) {
      // The year is a reading and not a statement: it is the twelve added up, and the way to change
      // it is to change one of them.
      if (!this.monthsEditable || index >= this.bars.length) return
      const entry = this.bars[index]
      if (!entry || !entry.parts) return
      if (this.editing === index) {
        this.closeCard()
        return
      }
      this.editing = index
      this.$nextTick(() => this.focusFirst())
    },
    focusFirst () {
      this.focusPart(this.cardParts[0])
    },
    focusLast () {
      this.focusPart(this.cardParts[this.cardParts.length - 1])
    },
    focusPart (part) {
      const el = part ? this.fieldFor(part.index) : null
      if (el) {
        el.focus()
        el.select()
      }
    },
    // ONE ROW'S FIELD, and the reason it is not simply `$refs[key]`. A ref inside a v-for is
    // registered as an array and Vue appends to it before it splices the departing node out, so for
    // the length of a patch the key can hold both the input that is leaving and the one that is
    // arriving — which is exactly what stepping from one month to the next does, the rows being
    // keyed by the month. The one still in the document is the one meant.
    fieldFor (index) {
      const field = this.$refs[`field_${index}`]
      const list = Array.isArray(field) ? field : [field]
      const live = list.filter(node => node && node.isConnected)
      return live.length > 0 ? live[live.length - 1] : list[list.length - 1] || null
    },
    // The card let go of without a word: everything typed into it is dropped, which is what Escape
    // has always meant and the reason the fields are not committed one at a time.
    closeCard () {
      this.editing = null
    },
    // BOTH FIGURES AT ONCE, on the way out. The two rows hold each other still — what one may claim
    // in a month is read against what the other already has — so a card that sent each field as it
    // was left would hold the second against a year the first had already moved. What is being
    // stated is the column.
    //
    // Only what actually changed goes up, compared as TEXT against exactly what the field was opened
    // on — the one comparison a component with no arithmetic can make — so a card opened and shut
    // again says nothing at all.
    commitCard () {
      const at = this.editing
      if (at === null) return
      const values = this.readCard(at)
      this.editing = null
      this.hovered = null
      this.sendCard(at, values)
    },
    // What the fields of one column say, against the figures they were opened on. Read off the
    // COLUMN rather than off `cardParts`, so it does not depend on which month is currently pinned:
    // stepping to the next one has to gather this one first, and a reader of the open card would be
    // gathering the month it is about to move to.
    readCard (at) {
      const bar = this.bars[at]
      const parts = bar && bar.parts ? bar.parts : []
      const values = []
      parts.forEach(part => {
        const el = this.fieldFor(part.index)
        if (!el) return
        const typed = String(el.value === null || el.value === undefined ? '' : el.value).trim()
        if (typed === '' || typed === String(part.value).trim()) return
        values.push({ index: part.index, value: typed })
      })
      return values
    },
    // A column handed up, however the card was left: on Enter, on a blur, or on the Tab that opened
    // the next month. A card opened and shut again says nothing at all.
    sendCard (at, values) {
      if (values.length === 0) return
      // Held over the flush the parent's redraw takes, so the watcher below cannot read the row
      // moving under a card that has already closed or moved itself as a reason to close it again.
      this.closing = true
      this.$nextTick(() => { this.closing = false })
      this.$emit('month', { index: at, values: values })
    },
    // TAB AT EITHER END OF THE CARD, which is where it stops being the browser's business. In the
    // middle of the stack nothing is bound: the fields are in the DOM in the order they are drawn in
    // and the default does exactly the right thing. Off the last one — or off the first one
    // backwards — it is the row's own step, and December is followed by January.
    onTab (event, position, step) {
      const last = this.cardParts.length - 1
      if (step > 0 ? position < last : position > 0) return
      event.preventDefault()
      this.stepCard(step)
    },
    // THE CARD CARRIED TO THE NEXT MONTH. What was typed goes up exactly as Enter would have sent
    // it, and then the card reopens one column along — the same gesture the two rows above this one
    // answer to, said about a card rather than about a single box.
    //
    // The card does NOT close on the way. The commit moves the row, and `bars` closes a pinned card
    // whenever the row it was opened on is replaced — which is right for a revenue retyped or a
    // channel dropped, and exactly wrong here; `closing` holds it off for the one flush that redraw
    // takes. The focus is taken afterwards rather than by the browser, the fields of the month being
    // stepped to not existing until it has.
    stepCard (step) {
      const count = this.bars.length
      const at = this.editing
      if (at === null || count === 0) return
      const values = this.readCard(at)
      this.stepping = true
      this.editing = (at + step + count) % count
      this.hovered = null
      this.sendCard(at, values)
      this.$nextTick(() => {
        if (step > 0) this.focusFirst()
        else this.focusLast()
        this.stepping = false
      })
    },
    // THE CARD LEFT, however it was left: a click somewhere else on the page, or a Tab off the last
    // field. Both are the same gesture — a reader who typed and looked away must not be left with a
    // caret over a chart — and both commit, which is the bargain every other field in this wizard
    // keeps. Tab BETWEEN the fields of one card is not leaving it and says nothing: the focus is
    // still inside the box, which is what the containment test below asks.
    onCardBlur (event) {
      if (this.editing === null || this.stepping) return
      const card = this.$refs.card
      const next = event.relatedTarget
      if (card && next && card.contains(next)) return
      this.commitCard()
    }
  },
  watch: {
    // A card belongs to the row it was opened on. Committing closes it already; this is for the ways
    // the row changes that are not one — the revenue retyped, a curve edited, a year pasted into the
    // table under it — which would otherwise leave a form standing over somebody else's month.
    bars () {
      if (this.closing) return
      this.editing = null
    }
  }
}
</script>

<style scoped>
  .shape-months {
    margin-top: 10px;
  }
  /* A row of its own height rather than whatever is left of a card, and taller than the spend's:
     this one is read on both sides of a line, so half of it is what that row gets whole. */
  .shape-months__bars {
    height: 168px;
    margin-top: 4px;
  }
  .clone-metric__bars {
    position: relative;
    display: flex;
    align-items: stretch;
  }
  /* A span where the row is only read and a button where it can be typed into, laid out by one rule:
     the browser's own button chrome is taken off so the two are the same column either way. The two
     halves are equal, the axis being symmetric, so each is half the column and neither grows. */
  .clone-metric__bar {
    flex: 1 1 0;
    min-width: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    outline: none;
  }
  .clone-metric__bar--live {
    cursor: pointer;
  }
  /* THE COLUMN THAT IS NOT A MONTH, set apart by a rule down its left. It is the same two claims
     drawn the same way, at its OWN scale — a year of seasonality is not a month of it — and the rule
     is what says the two axes are not one. Its width and the air before it are the revenue row's
     own, so the rule under this plot and the rule under that one stand on one line and both plots
     end at the same place. */
  .clone-metric__bar--apart {
    padding-left: 12px;
    border-left: 1px solid rgba(0, 0, 0, 0.08);
  }
  /* The column under the pointer and the one whose card is open, lit the same way: the bands keep
     their own fills — the split is what the stack is FOR — and the column is picked out from behind
     them instead. */
  .clone-metric__bar:hover,
  .clone-metric__bar--editing {
    background-color: rgba(0, 0, 0, 0.06);
  }
  .clone-metric__bar--live:focus-visible {
    box-shadow: 0 0 0 2px rgba(245, 127, 23, 0.45);
  }
  /* The two regions of a column, above the line and below it. They are laid out by the column's own
     flex, so a month's figure is still a flex item of the same box as its segments and a bar of no
     height still has its number standing over the line rather than under it. */
  .clone-metric__bar-up {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
  /* Read downwards from the line, which is why it starts where the other one ends. */
  .clone-metric__bar-down {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
  /* The line itself, through the middle of the row. Darker than the row's ground, because which side
     of it a month falls on is the first thing to read here, and deaf to the pointer so the column
     under it keeps its click. */
  .clone-metric__bar-zero {
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.25);
    pointer-events: none;
  }
  /* Sized by flex-basis and not by height, so the figure over the stack is a flex item of the same
     column and a month at nothing still has its number standing over the axis. Neither shrinks:
     what the row states is what is drawn. */
  .clone-metric__bar-fill {
    display: block;
    width: 100%;
    flex-grow: 0;
    flex-shrink: 0;
  }
  /* THE SECOND ROW'S FILL, and the whole of what tells the two apart. Stripes and not a second hue:
     these two years are one bar of the sales decomposition, one band of the revenue columns and one
     pot on the meter above, so a second colour here would be this chart claiming a distinction the
     rest of the page does not make. Cut in white rather than in a darker shade, so it reads as the
     same band with a texture on it at three pixels tall. */
  .clone-metric__bar-fill--hatched {
    background-image: repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.85) 0,
      rgba(255, 255, 255, 0.85) 2px,
      rgba(255, 255, 255, 0) 2px,
      rgba(255, 255, 255, 0) 5px
    );
  }
  /* THE MONTH'S OWN FIGURE, OVER ITS OWN COLUMN. Small and grey: it is twelve numbers at once and
     what is compared across them is the shape, which is drawn — the figures are what that shape is
     worth, read one at a time. It is the two rows SUMMED, which is what the column's height is: the
     split between them is the card's to give. */
  .clone-metric__bar-value {
    flex: 0 0 auto;
    padding-bottom: 2px;
    font-size: 0.55rem;
    line-height: 1.1;
    letter-spacing: -0.01em;
    color: rgba(0, 0, 0, 0.5);
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .clone-metric__bar-axis {
    display: flex;
    margin-top: 3px;
  }
  .clone-metric__bar-tick {
    flex: 1 1 0;
    min-width: 0;
    text-align: center;
    font-size: 9px;
    line-height: 1.1;
    color: rgba(0, 0, 0, 0.45);
    overflow: hidden;
  }
  .clone-metric__bar-axis--dense .clone-metric__bar-tick {
    font-size: 7px;
  }
  /* And the word under the year, in the room the column above it takes. It is not one of twelve of
     anything, so it keeps its name rather than an initial, and reads in the small grey the other
     readings wear rather than as a thirteenth tick. */
  .clone-metric__bar-tick--apart {
    padding-left: 12px;
    font-size: 0.65rem;
    color: rgba(0, 0, 0, 0.5);
    text-align: left;
  }

  /* WHAT THE MONTH GAVE TO EACH OF THE TWO, in a card rather than a line: the month, each row beside
     the fill its band is drawn in, and what they come to under a rule. */
  .clone-bar-tip {
    position: absolute;
    bottom: calc(100% + 8px);
    z-index: 2;
    padding: 7px 11px 8px;
    border-radius: 4px;
    color: #ffffff;
    background-color: rgba(33, 33, 33, 0.94);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.26);
    pointer-events: none;
  }
  /* A reading is deaf to the pointer so that moving onto it never counts as leaving the column that
     opened it. A form cannot be: it is the thing being reached for. */
  .clone-bar-tip--live {
    pointer-events: auto;
  }
  .clone-bar-tip__head {
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.4;
    margin-bottom: 3px;
  }
  .clone-bar-tip__row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.75rem;
    line-height: 1.6;
  }
  /* The sum, under a rule: it is not a third claim, it is what the two above it come to. */
  .clone-bar-tip__row--sum {
    margin-top: 3px;
    padding-top: 3px;
    border-top: 1px solid rgba(255, 255, 255, 0.25);
    font-weight: 600;
  }
  .clone-bar-tip__dot {
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  /* The hatched row's dot, carrying the same stripes its band does — at eight pixels across, a
     single diagonal is all there is room for and all that is needed. */
  .clone-bar-tip__dot--hatched {
    background-image: repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.9) 0,
      rgba(255, 255, 255, 0.9) 1px,
      rgba(255, 255, 255, 0) 1px,
      rgba(255, 255, 255, 0) 3px
    );
  }
  .clone-bar-tip__dot--none {
    background-color: transparent;
  }
  .clone-bar-tip__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  /* The figures in one column and of one width, so a stack of amounts reads as a column of amounts
     rather than as a paragraph of sentences. */
  .clone-bar-tip__value {
    flex: none;
    font-variant-numeric: tabular-nums;
  }
  /* THE ROW'S OWN FIGURE, TYPED, in the row that names it. It stands exactly where the figure it
     replaces stood and is the same width as the widest of them, so the column of amounts stays a
     column while it is being written into: a field that grew with its content would move the other
     row's decimal point as it was typed. Drawn on the card's own dark ground rather than on white —
     this is one card and not a form laid over one. */
  .clone-bar-tip__field {
    flex: none;
    width: 72px;
    padding: 1px 5px;
    font-size: 0.75rem;
    line-height: 1.4;
    font-variant-numeric: tabular-nums;
    text-align: right;
    color: #ffffff;
    background-color: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 3px;
    outline: none;
  }
  .clone-bar-tip__field:focus {
    border-color: #ffffff;
    background-color: rgba(255, 255, 255, 0.2);
  }
  /* What the three keys do, at the foot of the form and in the quietest voice the card has: it is an
     instruction and not a figure, and the figures above it are what the card is for. */
  .clone-bar-tip__keys {
    margin-top: 5px;
    font-size: 0.66rem;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.6);
  }

  /* The key to the two fills, in the small grey every note on this page is written in: it explains
     the picture and is not part of it. */
  .shape-months__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 16px;
    margin-top: 6px;
    font-size: 0.7rem;
    color: rgba(0, 0, 0, 0.5);
  }
  .shape-months__legend-key {
    display: inline-flex;
    align-items: center;
  }
  .shape-months__swatch {
    display: inline-block;
    flex: 0 0 auto;
    width: 9px;
    height: 9px;
    margin-right: 5px;
    border-radius: 2px;
  }
  .shape-months__swatch--hatched {
    background-image: repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.85) 0,
      rgba(255, 255, 255, 0.85) 2px,
      rgba(255, 255, 255, 0) 2px,
      rgba(255, 255, 255, 0) 5px
    );
  }
</style>
